// FCP design system — Input/TextArea/Select primitives (v2.8.0)

import React from 'react';
import './Field.css';

/**
 * Which host elements a <label for=…> can actually address.
 *
 * A `<div>` wrapping the real control is the case that matters. Field stamps
 * the id on its child, and Login's child is a positioning `<div>` around an
 * `<Input id="login-password">` — so TWO elements carried
 * `id="login-password"`, the div came first in document order, and the label's
 * `for` resolved to it. Tapping the word "Password" on the sign-in screen
 * focused nothing. `Login.test.js` had to address the input through
 * `.login-password input` because `#login-password` returned the wrapper.
 *
 * A COMPONENT child (function or forwardRef — `Input`, `TextArea`, `Select`)
 * always gets the id: it is the component's job to put it somewhere useful,
 * and `Select` in particular renders its own wrapper and spreads the id onto
 * the `<select>` inside. That case is pinned in Field.test.js.
 */
const LABELABLE_HOST = new Set(['input', 'select', 'textarea', 'button', 'meter', 'output', 'progress']);
const takesTheId = (child) => typeof child.type !== 'string' || LABELABLE_HOST.has(child.type);

/**
 * Field — wrapper around a label + input + helper/error text.
 * Use directly for one-off inputs, or compose <Input>/<TextArea>/<Select>
 * inside it for explicit control.
 *
 * ── Three defects fixed here on 2026-09-06, all of them silent ─────────────
 *
 * 1. The generated id was `Math.random()` **in the render body**, so it was a
 *    different string on every render (#296). React reuses the DOM nodes, so
 *    the label's `for` and the input's `id` were rewritten together and stayed
 *    in agreement — but nothing else could hold a reference to it, and any
 *    render between a screen reader reading the label and the user reaching
 *    the control moved the target. `useId` is what React 18 provides for
 *    exactly this and is stable for the life of the component.
 *
 * 2. `<Field label="x"><Input id="mine" /></Field>` put "mine" on the input
 *    (`child.props.id || id`) while the LABEL pointed at the generated id, so
 *    the label addressed nothing at all (#120 §1). The id now prefers, in
 *    order: what the caller named in `htmlFor`, the child's own id, then a
 *    generated one — so the two sides cannot disagree.
 *
 * 3. A caller's own `aria-describedby` was overwritten with `undefined`
 *    whenever there was no error and no hint (#120 §2), because `cloneElement`
 *    treats an explicit undefined as an override. It is now left alone unless
 *    this component has something of its own to point at.
 */
export function Field({ label, htmlFor, hint, error, required, children, className = '' }) {
  const generated = React.useId();
  const kids = React.Children.toArray(children);
  const firstElement = kids.find((c) => React.isValidElement(c));
  const id = htmlFor || (firstElement && firstElement.props.id) || generated;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : null;

  let stamped = false;
  const enrichedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const props = {};
    // Only the FIRST child that can hold it, and never over one it already
    // has: two children sharing one id is the same ambiguity as the wrapper.
    if (!stamped && !child.props.id && takesTheId(child)) {
      props.id = id;
      stamped = true;
    }
    if (error) props['aria-invalid'] = 'true';
    if (describedBy) props['aria-describedby'] = describedBy;
    return React.cloneElement(child, props);
  });

  return (
    <div className={`fcp-field ${error ? 'fcp-field--error' : ''} ${className}`}>
      {label && (
        <label htmlFor={id} className="fcp-field__label">
          {label}
          {required && <span className="fcp-field__required" aria-hidden="true"> *</span>}
        </label>
      )}
      {enrichedChildren}
      {error && <div id={`${id}-error`} className="fcp-field__error">{error}</div>}
      {!error && hint && <div id={`${id}-hint`} className="fcp-field__hint">{hint}</div>}
    </div>
  );
}

export const Input = React.forwardRef(function Input(
  { size = 'md', className = '', ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      className={`fcp-input fcp-input--${size} ${className}`}
      {...rest}
    />
  );
});

export const TextArea = React.forwardRef(function TextArea(
  { rows = 4, className = '', ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={`fcp-textarea ${className}`}
      {...rest}
    />
  );
});

export const Select = React.forwardRef(function Select(
  { size = 'md', children, className = '', ...rest },
  ref
) {
  return (
    <div className="fcp-select-wrap">
      <select
        ref={ref}
        className={`fcp-select fcp-select--${size} ${className}`}
        {...rest}
      >
        {children}
      </select>
      <span className="fcp-select__chevron" aria-hidden="true">
        <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
          <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  );
});
